import { sdkRequest } from '@eventbrite/http';
import { formatQuery } from 'url-lib';
import { GA_EVENTBRITE } from '../constants/analytics';
import { fetchAuthentication, IFetchJSONErrorProps } from './utils';

export const SEND_RESET_PASSWORD_URL = '/api/v3/users/password_reset_email/';
export const SEND_LINK_ACCOUNT_URL = '/api/v3/users/send_link_account_email/';
export const SEND_SOFT_ACCOUNT_ACTIVATION_EMAIL_URL =
    '/users/send_activation_email/';
export const SEND_PASSWORDLESS_EMAIL_URL =
    '/api/v3/users/send_passwordless_login_email/';
// todo
export const SEND_VERIFICATION_EMAIL_URL =
    '/api/v3/users/send_verification_code/';

export interface ISoftAccountEmailApiQueryProps {
    email: string;
    destination?: string;
}

export interface ISoftAccountEmailApiResponseProps {
    activationEmailSent: boolean;
    errors: any;
    userExists: boolean;
}

interface ISoftAccountEmailApiRawResponseProps {
    activation_email_sent: boolean;
    errors: any;
    user_exists: boolean;
}

/**
 * API to send email to reset password
 */
export const sendResetPasswordEmail = ({
    email,
    mustLinkAccount = false,
    socialProvider,
}: {
    email: string;
    mustLinkAccount?: boolean;
    socialProvider?: string;
}) => {
    const body: {
        email: string;
        application: string;
        must_link_account: boolean;
        external_provider?: string;
    } = {
        email,
        application: 'web',
        must_link_account: mustLinkAccount,
    };

    // external_provider is only sent for facebook and apple
    if (socialProvider && socialProvider !== GA_EVENTBRITE) {
        body['external_provider'] = socialProvider;
    }

    return sdkRequest(SEND_RESET_PASSWORD_URL, {
        method: 'POST',

        body: JSON.stringify(body),
    })
        .then((response: any) => response)
        .catch((error: any) => {
            const statusCode = error.response ? error.response.status : 400;
            if (statusCode === 200) {
                // 200 Code It's Ok
                return Promise.resolve();
            } else {
                throw error;
            }
        });
};

/**
 * API to send email activation for Soft Account
 */
export const sendSoftAccountActivationEmail = ({
    email,
    destination = '',
}: ISoftAccountEmailApiQueryProps) =>
    fetchAuthentication(SEND_SOFT_ACCOUNT_ACTIVATION_EMAIL_URL, {
        method: 'POST',
        body: formatQuery({ email, destination }),
    })
        .then((response: ISoftAccountEmailApiRawResponseProps) => {
            const _response: ISoftAccountEmailApiResponseProps = {
                activationEmailSent: response?.['activation_email_sent'],
                errors: response?.errors,
                userExists: response?.['user_exists'],
            };

            return _response;
        })
        .catch((error: IFetchJSONErrorProps) => {
            // Return the error as a parseed error or an empty object to prevent failures
            throw error.errors || {};
        });

/**
 *API to send Magic Link to link account
 */
export const sendEmailLinkAccount = ({
    email,
    socialProvider,
}: {
    email: string;
    socialProvider: string;
}) =>
    sdkRequest(SEND_LINK_ACCOUNT_URL, {
        method: 'POST',
        body: JSON.stringify({
            email: email,
            external_provider: socialProvider,
        }),
    });

export const sendPasswordlessEmail = (email: string) => {
    return sdkRequest(SEND_PASSWORDLESS_EMAIL_URL, {
        method: 'POST',
        body: JSON.stringify({ email }),
    })
        .then((response: any) => response)
        .catch((error: any) => {
            const statusCode = error.response ? error.response.status : 400;
            if (statusCode === 200 || statusCode === 204) {
                // 200/204 Code It's Ok
                return Promise.resolve();
            } else {
                throw error;
            }
        });
};

export const sendVerificationEmail = ({
    email,
    target,
}: {
    email: string;
    target: string;
}) => {
    return sdkRequest(SEND_VERIFICATION_EMAIL_URL, {
        method: 'POST',
        body: JSON.stringify({ email, target_flow: target }),
    })
        .then((response: any) => response)
        .catch((error: any) => {
            const statusCode = error.response ? error.response.status : 400;
            if (statusCode === 200 || statusCode === 204) {
                // 200/204 Code It's Ok
                return Promise.resolve();
            } else {
                throw error;
            }
        });
};
